import { useState } from 'react';
import { api } from '../services/api';

export const useTaskService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all tasks
  const tasks = {
    // Get current user
    getCurrentUser: async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/otms/personnel/current');
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting current user');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get board info
    getBoardInfo: async (params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/board/info${params ? `?${params}` : ''}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching board info');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get all tasks
    getAll: async (params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/info${params ? `?${params}` : ''}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching tasks');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get task by ID
    getById: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/info/${id}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create new task
    create: async (taskData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/tasks/create', taskData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating task');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update task
    update: async (id, taskData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/update/${id}`, taskData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating task');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete task
    delete: async (id) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/tasks/delete/${id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting task');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get subtasks for a task
    getSubtasks: async (parentId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/subtasks/info/${parentId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching subtasks');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create subtask
    createSubtask: async (parentId, taskData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/tasks/create', {
          ...taskData,
          Task_id: parentId,
          hasParent: true
        });
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating subtask');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };
  const taskType = {
    // Get all task types
    getAll: async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/otms/tasks/type/info');
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task types');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create a new task type
    create: async (taskTypeData) => {
      try {
        setLoading(true);
        setError(null);
        // First create the task type
        const taskTypeResponse = await api.post('/otms/tasks/type/create', {
          name: taskTypeData.name,
          description: taskTypeData.description
        });

        // Then create the task type structure
        const structureResponse = await api.post('/otms/tasks/structure/create', {
          taskTypeId: taskTypeResponse.data.id,
          definition: taskTypeData.definition,
          version: 1
        });

        return { 
          data: {
            ...taskTypeResponse.data,
            structure: structureResponse.data
          }
        };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating task type');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create a new version of an existing task type
    createVersion: async (taskTypeId, taskTypeData) => {
      try {
        setLoading(true);
        setError(null);
        
        // Create a new structure version for the task type
        const structureResponse = await api.post('/otms/tasks/structure/create', {
          taskTypeId: taskTypeId,
          definition: taskTypeData.definition,
          version: taskTypeData.version || 1
        });

        // Update the task type details if needed
        const taskTypeResponse = await api.put(`/otms/tasks/type/update/${taskTypeId}`, {
          name: taskTypeData.name,
          description: taskTypeData.description
        });

        return { 
          data: {
            ...taskTypeResponse.data,
            structure: structureResponse.data
          }
        };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating task type version');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get structures for a task type
    getStructures: async (taskTypeId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/structure/type/${taskTypeId}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task type structures');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const taskAssignment = {
    // Get all assignments for a task
    getByTaskId: async (taskId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/assignment/info/${taskId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task assignments');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create task assignment
    create: async (taskId, assignmentData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/tasks/assignment/create', {
          taskId,
          ...assignmentData
        });
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating task assignment');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update task assignment
    update: async (taskId, assignmentId, assignmentData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/assignment/update/${assignmentId}`, assignmentData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating task assignment');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete task assignment
    delete: async (taskId, assignmentId) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/tasks/assignment/delete/${assignmentId}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting task assignment');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const taskStructure = {
    // Get task structure
    get: async (taskId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/tasks/structure/info/${taskId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task structure');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update task structure
    update: async (taskId, structureData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/structure/update/${taskId}`, structureData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating task structure');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const taskResponse = {
    // Get all responses for a task (with pagination support)
    getAll: async (taskId, params = {}) => {
      try {
        setLoading(true);
        setError(null);
        // Support pagination: limit, beforeId, order
        const queryParams = new URLSearchParams();
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.beforeId) queryParams.append('beforeId', params.beforeId);
        if (params.order) queryParams.append('order', params.order);
        
        const queryString = queryParams.toString();
        const response = await api.get(`/otms/tasks/response/info/${taskId}${queryString ? `?${queryString}` : ''}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting task responses');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create new response
    create: async (taskId, responseData) => {
      try {
        setLoading(true);
        setError(null);
        
        // If responseData contains FormData (for audio/files), send it directly
        if (responseData.formData) {
          const response = await api.post(`/otms/tasks/response/create/${taskId}`, responseData.formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          return { data: response.data };
        }
        
        // Otherwise, send as JSON
        const response = await api.post(`/otms/tasks/response/create/${taskId}`, responseData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating task response');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update response
    update: async (taskId, responseId, responseData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/response/update/${responseId}`, responseData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating task response');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    updateMetadata: async (taskId, responseId, metadata) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/response/metadata/${responseId}`, { metadata });
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating response metadata');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete response
    delete: async (responseId) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/tasks/response/delete/${responseId}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting task response');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    updateReaction: async (responseId, emoji) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/tasks/response/reaction/${responseId}`, { emoji });
        return response.data;
      } catch (err) {
        console.error('Reaction update error:', err);
        setError(err.response?.data?.message || 'Error updating reaction');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    loading,
    error,
    tasks,
    taskType,
    taskAssignment,
    taskStructure,
    taskResponse
  };
};

export default useTaskService;